import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import '../styles/App.css';
import '../styles/custom_form.css';
import '../styles/FreeEventRegistrationForm.css';
const RegistrationForm = () => {
  const [formFields, setFormFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [registrationId, setRegistrationId] = useState('');
  const [attendeeName, setAttendeeName] = useState('');
  const [attendeeEmail, setAttendeeEmail] = useState('');
  const successRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const saved = localStorage.getItem('eventfork_registration_default_schema');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setFormFields(parsed);
          setLoading(false);
          return;
        }
      }

      // Fallback to default fields (same as builder)
      setFormFields([
        { id: 1, label: 'Full Name', type: 'text', required: true, placeholder: 'Enter your full name' },
        { id: 2, label: 'Email Address', type: 'email', required: true, placeholder: 'Enter your email' },
        { id: 3, label: 'Phone Number', type: 'tel', required: true, placeholder: 'Enter your phone number' },
        { id: 4, label: 'Organization', type: 'text', required: false, placeholder: 'Enter your organization name' },
      ]);
    } catch (e) {
      console.error('Failed to load registration form schema', e);
      setError('Unable to load registration form. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (fieldId, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Derive attendee name & email from fields if possible
    let nameFromForm = '';
    let emailFromForm = '';

    formFields.forEach((field) => {
      const value = formData[field.id];
      if (!value) return;

      const labelLower = (field.label || '').toLowerCase();
      if (!nameFromForm && (labelLower.includes('name') || labelLower.includes('attendee'))) {
        nameFromForm = value;
      }
      if (!emailFromForm && (field.type === 'email' || labelLower.includes('email'))) {
        emailFromForm = value;
      }
    });

    const regId = `EVT-${Math.floor(100000 + Math.random() * 900000)}`;

    console.log('Registration submitted:', formData, { registrationId: regId });
    setRegistrationId(regId);
    setAttendeeName(nameFromForm);
    setAttendeeEmail(emailFromForm);
    setSuccess(true);
  };

  const handleDownloadTicket = async () => {
    if (!successRef.current) return;
    try {
      const canvas = await html2canvas(successRef.current, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const maxWidth = pageWidth - 20; // 10mm margins left/right
      const maxHeight = pageHeight - 20; // 10mm margins top/bottom

      // Compute scale so image fits within both width and height
      const widthScale = maxWidth / canvas.width;
      const heightScale = maxHeight / canvas.height;
      const scale = Math.min(widthScale, heightScale);

      const imgWidth = canvas.width * scale;
      const imgHeight = canvas.height * scale;
      const marginX = (pageWidth - imgWidth) / 2;
      const marginY = (pageHeight - imgHeight) / 2;

      pdf.addImage(imgData, 'PNG', marginX, marginY, imgWidth, imgHeight);
      pdf.save('event-ticket.pdf');
    } catch (e) {
      console.error('Failed to generate ticket PDF', e);
      alert('Unable to generate ticket PDF. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="create-event-page">
        <div className="container create-event-container">
          <p>Loading registration form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="create-event-page">
        <div className="container create-event-container">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const showForm = !success;

  return (
    <div className="create-event-page">
      <div className="container create-event-container">
        <div className="preview-panel" style={{ width: '100%' }}>
          <div className="preview-container">
            {showForm && (
              <form onSubmit={handleSubmit} className="event-form">
                <h3>
                  <span role="img" aria-label="ticket">🎟️</span> Event Registration Form
                </h3>
                <p className="form-description">
                  Please fill out all required fields to register for the event.
                </p>

                {formFields.map((field) => (
                  <div key={field.id} className="form-group">
                    <label htmlFor={`field-${field.id}`}>
                      {field.label}
                      {field.required && <span className="required-star"> *</span>}
                    </label>

                    {field.type === 'textarea' ? (
                      <textarea
                        id={`field-${field.id}`}
                        value={formData[field.id] || ''}
                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                        placeholder={field.placeholder}
                        required={field.required}
                        rows="4"
                      />
                    ) : field.type === 'select' ? (
                      <select
                        id={`field-${field.id}`}
                        value={formData[field.id] || ''}
                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                        required={field.required}
                      >
                        <option value="">Select {field.label.toLowerCase()}</option>
                        {field.options.map((option, index) => (
                          <option key={index} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    ) : field.type === 'radio' ? (
                      <div className="radio-group">
                        {field.options.map((option, index) => (
                          <label key={index} className="radio-option">
                            <input
                              type="radio"
                              name={`field-${field.id}`}
                              value={option}
                              checked={formData[field.id] === option}
                              onChange={(e) => handleInputChange(field.id, e.target.value)}
                              required={field.required}
                            />
                            {option}
                          </label>
                        ))}
                      </div>
                    ) : field.type === 'checkbox' ? (
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData[field.id] || false}
                          onChange={(e) => handleInputChange(field.id, e.target.checked)}
                        />
                        {field.label}
                      </label>
                    ) : (
                      <input
                        type={field.type}
                        id={`field-${field.id}`}
                        value={formData[field.id] || ''}
                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                        placeholder={field.placeholder}
                        required={field.required}
                      />
                    )}
                  </div>
                ))}

                <div className="form-actions">
                  <button type="submit" className="submit-btn">
                    Register Now
                  </button>
                </div>
              </form>
            )}

            {!showForm && (
              <div className="registration-success-overlay">
                <div className="registration-success-card" ref={successRef}>
                  <div className="success-section">
                    <div className="success-icon">
                      <i className="fas fa-check" />
                    </div>

                <h2 className="success-title">Registration Confirmed!</h2>
                <p className="success-subtitle">
                  You're all set for the event. Check your email for confirmation details.
                </p>

                <div className="success-details">
                  <div className="detail-item">
                    <span className="detail-label">Event:</span>
                    <span className="detail-value">EventFork Conference</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Date &amp; Time:</span>
                    <span className="detail-value">March 15-16, 2025 • 9:00 AM IST</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Venue:</span>
                    <span className="detail-value">Bangalore International Centre</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Registration ID:</span>
                    <span className="detail-value">{registrationId}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Attendee:</span>
                    <span className="detail-value">{attendeeName || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Email:</span>
                    <span className="detail-value">{attendeeEmail || '-'}</span>
                  </div>
                </div>

                <div className="qr-container">
                  <div className="qr-placeholder">
                    <i
                      className="fas fa-qrcode"
                      style={{ fontSize: '3rem', marginBottom: 10, display: 'block' }}
                    />
                    QR Code preview (static for demo)
                  </div>
                  <p className="qr-instructions">
                    <i className="fas fa-info-circle" /> Show this QR code at the entrance
                  </p>
                </div>

                <div className="success-actions">
                  <button
                    className="action-btn btn-secondary"
                    type="button"
                    onClick={handleDownloadTicket}
                  >
                    <i className="fas fa-download" /> Download Ticket
                  </button>
                  <button
                    className="action-btn btn-primary"
                    type="button"
                    onClick={() => alert('For demo purposes, this would add the event to your calendar.')}
                  >
                    <i className="fas fa-calendar-plus" /> Add to Calendar
                  </button>
                </div>

                <div style={{ marginTop: 30, fontSize: '0.9rem', color: 'var(--gray)' }}>
                  <p>
                    <i className="fas fa-envelope" /> Confirmation email sent to: <span>{attendeeEmail || '-'}</span>
                  </p>
                </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationForm;
